/**
 * Customer dashboard session — stores the signed Studio customer token
 * (issued by /api/studio/account-login) and wraps fetches to the dashboard
 * proxies. Stateless by design: expiry/revocation is enforced by Studio
 * (24h TTL + token_version), so a 401 here simply means "sign in again".
 */

const TOKEN_KEY = 'studio_customer_token'

export function getCustomerToken(): string | null {
	if (typeof window === 'undefined') return null
	return window.localStorage.getItem(TOKEN_KEY)
}

export function setCustomerToken(token: string): void {
	window.localStorage.setItem(TOKEN_KEY, token)
}

export function clearCustomerToken(): void {
	window.localStorage.removeItem(TOKEN_KEY)
}

export class CustomerSessionExpired extends Error {}

/**
 * Fetch a dashboard proxy with the customer bearer token. Throws
 * CustomerSessionExpired on 401 (missing/expired/revoked token) — callers
 * clear the token and send the user back to /dashboard/login.
 */
export async function fetchWithCustomerAuth<T>(
	path: string,
	// Parameters<typeof fetch>[1] instead of the bare `RequestInit` name —
	// this project's ESLint config parses .ts files without TS-aware
	// no-undef handling, so a type-only global identifier like RequestInit
	// trips "no-undef" even though it's valid TypeScript.
	init?: Parameters<typeof fetch>[1]
): Promise<T> {
	const token = getCustomerToken()
	if (!token) throw new CustomerSessionExpired('No session')
	const res = await fetch(path, {
		...init,
		headers: {
			...init?.headers,
			Authorization: `Bearer ${token}`,
			...(init?.body ? { 'Content-Type': 'application/json' } : {}),
		},
	})
	if (res.status === 401) {
		clearCustomerToken()
		throw new CustomerSessionExpired('Session expired')
	}
	if (!res.ok) {
		throw new Error(`${path} failed: ${res.status}`)
	}
	return (await res.json()) as T
}

/** POST a dashboard proxy with the customer bearer token. Same session
 * semantics as fetchWithCustomerAuth (mirrors sponsorAuth.ts's
 * postWithSponsorAuth for this principal). */
export async function postWithCustomerAuth<T>(
	path: string,
	body: unknown
): Promise<T> {
	const token = getCustomerToken()
	if (!token) throw new CustomerSessionExpired('No session')
	const res = await fetch(path, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	})
	if (res.status === 401) {
		clearCustomerToken()
		throw new CustomerSessionExpired('Session expired')
	}
	if (!res.ok) {
		const errBody = await res.json().catch(() => ({}))
		throw new Error(
			(errBody as { detail?: string }).detail ?? `${path} failed: ${res.status}`
		)
	}
	return (await res.json()) as T
}
